import {
  DataGridPro,
  GridFilterModel,
  GridFooter,
  GridHeaderFilterCellProps,
  GridPaginationModel,
  GridRowModel,
  GridSortModel,
  GridToolbarContainer,
} from '@mui/x-data-grid-pro';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Chip, IconButton, MenuItem, Menu, Stack} from '@mui/material';
import {datagridColors} from '../../../assets/simpleDatagridStyles';
import Box from '@mui/material/Box';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import {
  AttachFileRounded,
  CancelRounded,
  CopyAllRounded,
  FileUploadRounded,
  FilterAltOutlined,
  SaveAltRounded,
  TextsmsRounded, WomanRounded,
} from '@mui/icons-material';
import {RejectConfirmDialog} from './RejectConfirmDialog';
import api from '../../../services/api';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {useFakeMutation} from '../hooks/useFakeMutation';
import SelectAddressField from './fields/SelectAddressField';
import {FileUploadDialog} from './FileUploadDialog';
import SelectDriverField from './fields/SelectDriverField';
import {useStateContext} from '../../../hooks/useStateContext';
import {exportToXLSX} from '../utils/exportToXLSX';
import toast from 'react-hot-toast';
import SelectVehicleField from './fields/SelectVehicleField';
import Tooltip from '@mui/material/Tooltip';
import {AssignStaffDialog} from "./AssignStaffDialog";
import {io} from "socket.io-client";
import Badge from "@mui/material/Badge";
import {createNotification} from "../../../reducer/actions/createNotification";
import SelectStaffField from "./fields/SelectStaffField";

interface Props {
  rows: any;
  loading: boolean;
  onLoading: (value: boolean) => void;
  onChangeTab: (value: number) => void;
  onOpenChat: (value: any) => void;
  onRefresh: () => void;
  apiRef: any;
  customFields: any;
  editableFields: any;
  requiredFields: any;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const Table: React.ComponentType<Props> = ({
                                                    rows,
                                                    loading,
                                                    onLoading,
                                                    onRefresh,
                                                    onChangeTab,
                                                    apiRef,
                                                    customFields,
                                                    onOpenChat,
                                                    editableFields,
                                                    requiredFields,
                                                  }) => {
  const [typeOfTable, setTypeOfTable] = useState('default');
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [openAssignedStaffsDialog, setOpenAssignedStaffsDialog] = useState<any>(false);
  const [openFileUploadDialog, setOpenFileUploadDialog] = useState<any>(false);
  const [openHeaderFilters, setOpenHeaderFilters] = useState(false);
  const [value, setValue] = useState(0);
  const [loadingConfirm, setLoadingConfirm] = useState<boolean>(false);
  const [filterModelStatic, setFilterModelStatic] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModelStatic, setSortModelStatic] = useState<GridSortModel>([]);
  const [paginationModelStatic, setPaginationModelStatic] = useState<
    GridPaginationModel
  >({
    page: 0,
    pageSize: 10,
  });
  const {isMobile, user} = useStateContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [reservationIds, setReservationIds] = useState<number[]>([]);

  const hasVehicles = user?.service_providers?.some(
    (provider: any) => provider?.vehicles?.length > 0
  );

  const isFieldRequired = useCallback((fieldKey: string): boolean => {
    if (!requiredFields) return false;

    // ha tömb
    if (Array.isArray(requiredFields)) {
      return requiredFields.includes(fieldKey);
    }

    // ha sima objektum
    if (typeof requiredFields === 'object') {
      return Boolean(requiredFields[fieldKey]);
    }

    return false;
  }, [requiredFields]);

  const renderRequiredHeader = useCallback((label: string, fieldKey?: string) => {
    const required = fieldKey ? isFieldRequired(fieldKey) : false;

    return (
      <Stack spacing={0} alignItems="center">
        <div className={'MuiDataGrid-columnHeaderTitle'} style={{
          fontWeight: 'bold',
        }}>{label}</div>
        {required && (
          <Chip
            label="Kötelező"
            color="error"
            size="small"
            sx={{
              position: 'absolute',
              bottom: '5px',
              height: '18px',
              '& .MuiChip-label': {
                px: 0.75,
                fontSize: '10px',
                fontWeight: 700,
              },
            }}
          />
        )}
      </Stack>
    );
  }, [isFieldRequired]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    onChangeTab(newValue);
    setValue(newValue);
  };

  useEffect(() => {
    const uniqueFormattedTypes = rows
      .map((item: any) => item.formatted_type)
      .filter(
        (value: any, index: any, self: any) => self.indexOf(value) === index
      );

    let newTypeOfTable = 'default';

    if (
      uniqueFormattedTypes.some(
        (type: any) =>
          type === 'event-staff' || type === 'event-staff-airport-transfer'
      )
    ) {
      newTypeOfTable = 'event-staff';
    }

    if (
      uniqueFormattedTypes.some(
        (type: any) => type === 'transfer' || type === 'airport-transfer'
      )
    ) {
      newTypeOfTable = 'transfer';
    }

    setTypeOfTable(newTypeOfTable);
  }, [rows]);

  const hasAssignedStaffColumn = useMemo(() => {
    return rows?.some((row: any) => row?.has_assigned_staffs);
  }, [rows]);

  const columns = useMemo(() => {
    const columnArray: any = [];

    columnArray.push(
      {
        id: 1,
        field: 'row_number',
        headerName: '#',
        editable: false,
        type: 'text',
        width: 90,
        renderHeaderFilter: (params: GridHeaderFilterCellProps) => {
          return <></>;
        },
        renderHeader: (props: any) => {
          return (
            <IconButton
              size={'small'}
              onClick={() => {
                setOpenHeaderFilters(!openHeaderFilters);
              }}
            >
              <FilterAltOutlined
                sx={{
                  color: '#fff',
                }}
              />
            </IconButton>
          );
        },
        renderCell: (props: any) => {
          const currentPage = paginationModelStatic.page;
          const pageSize = paginationModelStatic.pageSize;

          const rowIndex =
            currentPage * pageSize +
            props.api.getRowIndexRelativeToVisibleRows(props.id) +
            1;
          const hasAttachment = props.row?.service_reservation?.files.length;
          const hasAssignedStaffs = props.row?.has_assigned_staffs;
          const isRejected = props.row?.confirmation_status?.id === 4;

          return (
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                '& .MuiIconButton-root': {
                  p: '1px'
                }
              }}
            >
              <b>{rowIndex}</b>
              <Tooltip title={'Melléklet'}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenFileUploadDialog(props?.row);
                  }}
                >
                  <AttachFileRounded fontSize="small" color={hasAttachment ? 'inherit' : 'disabled'}/>
                </IconButton>
              </Tooltip>

              <Tooltip title={'Chat megnyitása'}>
                <IconButton
                  size={'small'}
                  onClick={() => {
                    const rowData = {...props?.row};
                    rowData.id = props?.row?.service_reservation_id;
                    onOpenChat(rowData);

                    removeReservationId(props?.row?.service_reservation_id)
                  }}
                >
                  <Badge
                    color="error"
                    variant="dot" // pötty jelzés
                    invisible={!reservationIds.includes(props.row?.service_reservation_id)} // ha nincs benne, ne jelenjen meg
                  >
                    <TextsmsRounded color={'primary'} fontSize={'small'}/>
                  </Badge>

                </IconButton>
              </Tooltip>

              {(!isRejected && value !== 1) ? (
                <Tooltip title={'Foglalás elutasítása'}>
                  <IconButton
                    size={'small'}
                    onClick={() => {
                      setOpenConfirmDialog(props?.row);
                    }}
                  >
                    <CancelRounded color={'error'} fontSize={'small'}/>
                  </IconButton>
                </Tooltip>
              ) : <></>}
              {hasAssignedStaffs ? (
                <Tooltip title={'Fellépők hozzárendelése'}>
                  <IconButton
                    size={'small'}
                    onClick={() => {
                      setOpenAssignedStaffsDialog(props?.row);
                    }}
                  >
                    <WomanRounded color={'info'} fontSize={'small'}/>
                  </IconButton>
                </Tooltip>
              ) : <></>}

              {/* <Tooltip  title={'Fájl feltöltése'} >
              <IconButton
                size={'small'}
                onClick={() => {
                  setOpenFileUploadDialog(props?.row);
                }}
              >
                <FileUploadRounded color={'primary'}/>
              </IconButton>
            </Tooltip>*/}
            </Stack>
          );

          /* return <Stack
           direction={'row'}
           alignItems={'center'}
         >
           <b>{rowIndex}</b>
           {hasAttachment && (
             <IconButton size={'small'} disabled>
               <AttachFileRounded fontSize={'small'} color={'disabled'}/>
             </IconButton>
           )}
         </Stack>*/
        },
      },
      {
        id: 51,
        headerName: 'Státusz',
        field: 'confirmation_status',
        type: 'text',
        editable: false,
        sortable: false,

        valueGetter: (params: any) =>
          params.row.confirmation_status?.name || '',
        renderCell: (params: any) => {
          return (
            <Chip
              sx={{
                background:
                  params.row.confirmation_status?.id === 2
                    ? '#2e7d32'
                    : params.row.confirmation_status?.id === 3
                      ? '#ed6c02'
                      : '#d32f2f',
                color: '#fff',
              }}
              label={params.row.confirmation_status?.name}
            />
          );
        },
      },
      {
        id: 1,
        field: 'type',
        headerName: 'Típus',
        editable: false,
        type: 'text',

        renderCell: (props: any) => {
          return <b dangerouslySetInnerHTML={{__html: props?.value}}></b>;
        },
      }
    );

    if (user?.service_providers && user?.service_providers.length > 1) {
      columnArray.push({
        id: 555,
        field: 'service_provider',
        headerName: 'Szolgáltató',
        editable: false,
        type: 'text',

        renderCell: (props: any) => {
          return (
            <b dangerouslySetInnerHTML={{__html: props?.value?.name}}></b>
          );
        },
      });
    }

    if (hasAssignedStaffColumn) {
      columnArray.push({
        id: 99991,
        field: 'assigned_staffs',
        headerName: 'Assigned Staff',
        editable: false,
        type: 'text',
        renderCell: (props: any) => {
          if (!props?.row?.has_assigned_staffs) {
            return '-';
          }

          const staffs = props?.row?.assigned_staffs;

          if (Array.isArray(staffs) && staffs.length) {
            return staffs.map((staff: any) => staff?.selected_staff?.name).filter(Boolean).join(', ');
          }

          return 'Van hozzárendelve';
        },
      });
    }


    if (typeOfTable === 'transfer') {
      columnArray.push(
        {
          id: 2,
          field: 'service',
          headerName: 'Szolgáltatás',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 11,
          field: 'activity',
          headerName: 'Program',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 3,
          field: 'group',
          headerName: 'Csoport',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 4,
          field: 'size',
          headerName: 'Létszám',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 5,
          field: 'date',
          headerName: 'Dátum',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 6,
          field: 'pickup_time',
          headerName: 'Pickup',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 3,
          field: 'start_time',
          headerName: 'Kezdés',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 3,
          field: 'flight',
          headerName: 'Járatszám',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return (
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    props?.row.formatted_type === 'airport-transfer'
                      ? props?.value
                      : '',
                }}
              ></p>
            );
          },
        },
        {
          id: 3,
          field: 'meeting_place',
          headerName: 'Találkozási pont',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 3333,
          field: 'driver',
          headerName: 'Sofőr',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return (
              <p>
                {props?.row?.driver?.name
                  ? props?.row?.driver?.name
                  : props?.row?.driver}
              </p>
            );
          },
          renderEditCell: (props: any) => {
            return <SelectDriverField params={props}/>;
          },
        }
      );

      if (hasVehicles) {
        columnArray.push({
          id: 4444,
          field: 'vehicle',
          headerName: 'Jármű',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return (
              <p>
                {props?.row?.vehicle?.name
                  ? props.row.vehicle.name
                  : props?.row?.vehicle}
              </p>
            );
          },
          renderEditCell: (props: any) => {
            return <SelectVehicleField params={props}/>;
          },
        });
      }
    } else if (typeOfTable === 'event-staff') {
      columnArray.push(
        {
          id: 10,
          field: 'event_staff',
          headerName: 'Fellépő',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {

            const value = props?.value === '-' ? props?.row?.service_reservation?.selected_staff?.name : (props?.value?.name ? props?.value?.name : props?.value);

            return <p dangerouslySetInnerHTML={{__html: value}}></p>;
          },

          renderEditCell: (props: any) => {
            return <SelectStaffField
              params={{
                value: props?.service_reservation?.selected_staff,
                row: props?.row
              }}
              props={props}
              type={'table'}/>;
          },

        },
        {
          id: 11,
          field: 'activity',
          headerName: 'Program',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 2,
          field: 'service',
          headerName: 'Műsor',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 3,
          field: 'group',
          headerName: 'Csoport',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 5,
          field: 'date',
          headerName: 'Dátum',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 6,
          field: 'arrival_time',
          headerName: 'Érkezés',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return (
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    props?.row.formatted_type === 'event-staff-airport-transfer'
                      ? props?.row?.pickup_arrival_time
                      : props?.value,
                }}
              ></p>
            );
          },
        },
        {
          id: 6,
          field: 'start_time',
          headerName: 'Kezdés',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return (
              <p
                dangerouslySetInnerHTML={{
                  __html:
                    props?.row.formatted_type === 'event-staff-airport-transfer'
                      ? props?.row?.pickup_time
                      : props?.value,
                }}
              ></p>
            );
          },
        },
        {
          id: 34,
          field: 'size',
          headerName: 'Létszám',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        }
      );
    } else {
      columnArray.push(
        {
          id: 2,
          field: 'service',
          headerName: 'Szolgáltatás',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 3,
          field: 'group',
          headerName: 'Csoport',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 4,
          field: 'size',
          headerName: 'Létszám',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 5,
          field: 'date',
          headerName: 'Dátum',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        },
        {
          id: 6,
          field: 'start_time',
          headerName: 'Kezdés',
          editable: false,
          type: 'text',

          renderCell: (props: any) => {
            return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
          },
        }
      );
    }

    columnArray.push({
      id: 111,
      field: 'venue',
      headerName: 'Helyszín',
      editable: false,
      type: 'text',
      renderCell: (props: any) => {
        const defaultValue =
          props?.row.formatted_type === 'event-staff-airport-transfer'
            ? props?.row?.meeting_place
            : props?.row?.venue;

        let name = '';
        if (defaultValue?.name) {
          name = defaultValue?.name;
          if (
            defaultValue?.address_line1 &&
            defaultValue?.address_line1 != defaultValue?.name
          ) {
            name = `${name} (${defaultValue?.address_line1})`;
          }
        } else {
          name = defaultValue;
        }

        return <p dangerouslySetInnerHTML={{__html: name}}></p>;
      },
      renderEditCell: (props: any) => {
        return <SelectAddressField params={props}/>;
      },
    });

    columnArray.push({
      id: 111,
      field: 'guide',
      headerName: 'Guide',
      editable: false,
      type: 'text',
      renderCell: (props: any) => {
        return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
      },
    });

    columnArray.push({
      id: 111,
      field: 'notes',
      headerName: 'Notes',
      editable: false,
      type: 'text',
      renderCell: (props: any) => {
        return <p dangerouslySetInnerHTML={{__html: props?.value}}></p>;
      },
    });

    if (customFields) {
      customFields.forEach((customField: any, index: number) => {

        const baseColumn = {
          id: `${customField.column_name}_${index}`,
          field: customField?.column_name,
          headerName: customField?.name,
          renderHeader: () => renderRequiredHeader(customField?.name, customField?.column_name),
          editable: false,
          flex: 1,
        };

        if (customField?.type === 'string') {
          columnArray.push({
            ...baseColumn,
            type: 'string',
            renderCell: (props: any) => props?.value,
          });
        }

        if (customField?.type === 'boolean') {
          columnArray.push({
            ...baseColumn,
            type: 'boolean',
          });
        }

        if (customField?.type === 'driver') {
          columnArray.push({
            ...baseColumn,
            type: 'string',
            renderCell: (props: any) => (
              <div>{props?.value?.name ?? '-'}</div>
            ),
          });
        }

        if (customField?.type === 'float') {
          columnArray.push({
            ...baseColumn,
            type: 'number',
          });
        }

        if (customField?.type === 'text') {
          columnArray.push({
            ...baseColumn,
            type: 'string',
            renderCell: (props: any) => (
              <div dangerouslySetInnerHTML={{__html: props?.value ?? ''}}/>
            ),
          });
        }

        // 🔹 DATE mező
        if (customField?.type === 'date') {
          columnArray.push({
            ...baseColumn,
            type: 'date',
            valueGetter: (params: any) =>
              params?.value ? new Date(params.value) : null,
          });
        }

        // 🔹 TIME mező (HH:mm)
        if (customField?.type === 'time') {
          columnArray.push({
            ...baseColumn,
            type: 'string',
            renderCell: (props: any) => {
              if (!props?.value) return '-';
              return props.value.slice(0, 5); // pl. 14:30
            },
          });
        }

      });
    }

    const columnArrayTemp: any = columnArray.map((columnData: any) => {

      if (editableFields?.staff && columnData?.field === 'event_staff'){
        columnData.editable = true;
      }

      if (editableFields[columnData?.field]) {
        columnData.editable = true;
      }

      return columnData;
    });
    return columnArrayTemp;
  }, [
    rows,
    typeOfTable,
    openHeaderFilters,
    paginationModelStatic,
    filterModelStatic,
    sortModelStatic,
    customFields,
    editableFields,
    hasAssignedStaffColumn,
    reservationIds
  ]);

  const CustomFooter = () => {
    return (
      <Stack
        direction={'row'}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px',
        }}
      >
        <IconButton
          sx={{
            visibility: 'hidden',
          }}
          onClick={() => {
          }}
        >
          <AddIcon color={'primary'}/>
        </IconButton>
        <GridFooter/>
      </Stack>
    );
  };

  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  const onSubmit = useCallback(
    (reservationId: any, confirmationId: any) => {
      onLoading(true);
      setLoadingConfirm(true);
      api
        .post(
          `/service-provider/service-reservation-confirmations/${reservationId}/${confirmationId}`,
          {
            action: 'decline-confirmed',
          }
        )
        .then((result: any) => {
          onRefresh();
        })
        .catch((error: any) => {
        })
        .finally(() => {
          onLoading(false);
          setLoadingConfirm(false);
          setOpenConfirmDialog(false);
        });
    },
    [onRefresh, onLoading, setOpenConfirmDialog]
  );

  const handleRejectConfirmation = (confirmation: any) => {
    onSubmit(confirmation?.service_reservation_id, confirmation?.id);
  };

  const mutateRow = useFakeMutation();
  const [promiseArguments, setPromiseArguments] = useState<any>(null);

  const processRowUpdate = useCallback(
    (newRow: any, oldRow: any) => {
      // Immediately return new row to update UI
      mutateRow(newRow, oldRow);

      return newRow; // Optimistic update
    },
    [mutateRow]
  );
  const CustomToolbar = memo(() => {
    return (
      <GridToolbarContainer
        sx={{
          padding: '0px',
          gap: isMobile ? 0 : '4px',
          '& .MuiButton-root': {
            background: '#ededed',
            padding: '2px 4px',
            fontSize: '11px',
            '& .MuiSvgIcon-root': {
              fontSize: '1rem !important',
            },
            '&:hover': {
              background: '#e3e3e3',
            },
          },
        }}
      >
        {!isMobile && (
          <>
            <Button
              startIcon={<SaveAltRounded/>}
              onClick={() =>
                exportToXLSX(rows, columns, apiRef, 'TRAVEL-CITY Foglalások')
              }
            >
              Export
            </Button>

            <Button
              startIcon={<CopyAllRounded/>}
              onClick={() => handleGenerateClipboardData()}
            >
              Kijelölt sorok másolása
            </Button>
          </>
        )}

        <Box sx={{flexGrow: 1}}/>
      </GridToolbarContainer>
    );
  });

  async function copyToClipboard() {
    try {
      const selectedRows = apiRef.current.getSelectedRows();
      if (selectedRows.size === 0) return;

      const text = Array.from(selectedRows.values())
        .map(
          (row: any) =>
            columns
              .slice(2)
              .map((col: any) => {
                const rowData = row[col.field]?.name
                  ? row[col.field]?.name
                  : row[col.field];
                return typeof rowData === 'string' && rowData.includes('<')
                  ? stripHtml(rowData)
                  : rowData;
              })
              .join('\t') // tabbal elválasztva
        )
        .join('\n');
      try {
        await navigator.clipboard.writeText(text);
      } catch (error) {
        throw error;
      }

      return text;
    } catch (error) {
      throw error;
    }
  }

  const handleGenerateClipboardData = async () => {
    toast.promise(copyToClipboard(), {
      loading: 'Generating clipboard data...',
      success: <b>Copied to clipboard!</b>,
      error: <b>Error while generating data</b>,
    });
  };

  function fetchNotifications() {

    api.get('/notifications').then((result: any) => {
      const newIds = result.data
        .filter(
          (n: any) => {
            return n?.type === 'service-reservation-message' &&
              n?.service_reservation_id &&
              !reservationIds.includes(n.service_reservation_id);
          }
        )
        .map((n: any) => n.service_reservation_id);

      // ha van új id, egyszer adjuk hozzá
      if (newIds.length > 0) {
        setReservationIds((prev) => [...prev, ...newIds]);
      }
    });
  }

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const socket = io('https://socket-zoe.stagemporium.com');
    socket.on('notification', (data: any) => {

      if (data?.notified_user_id == user?.id && data?.data?.type === 'service-reservation-message') {
        const newId = data?.data?.service_reservation?.id;

        if (newId && !reservationIds.includes(newId)) {
          setReservationIds((prev) => [...prev, newId]);
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  // ID törlés eseményre
  const removeReservationId = (id: number) => {
    setReservationIds((prev) => prev.filter((rId) => rId !== id));
  };

  console.log(typeOfTable);

  return (
    <>
      <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Jelenlegi foglalások" {...a11yProps(0)} />
          <Tab label="Múltbéli foglalások" {...a11yProps(1)} />
          <Tab label="Törölt foglalások" {...a11yProps(2)} />
        </Tabs>
      </Box>
      <Box sx={datagridColors('calc(100vh - 325px)')}>
        <DataGridPro
          apiRef={apiRef}
          sx={{
            maxHeight: 'calc(100% - 70px)',
          }}
          loading={loading}
          rows={rows}
          getRowClassName={(params: any) => {
            const color =
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
            return `${color}`;
          }}
          disableColumnMenu={true}
          columns={columns}
          disableRowSelectionOnClick
          columnHeaderHeight={Object.keys(requiredFields).length ? 60 : 38}
          getRowHeight={() => 'auto'}
          pagination
          editMode={'cell'}
          slots={{
            // @ts-ignore
            columnSortedDescendingIcon: CustomSortIconDesc,
            columnSortedAscendingIcon: CustomSortIconAsc,
            columnUnsortedIcon: CustomSortIcon,
            footer: CustomFooter,
            toolbar: CustomToolbar,
            headerFilterMenu: null,
          }}
          unstable_headerFilters={openHeaderFilters}
          pageSizeOptions={[25, 50, 100]}
          columnBuffer={50}
          onFilterModelChange={(newModel) => setFilterModelStatic(newModel)}
          onSortModelChange={(newModel) => setSortModelStatic(newModel)}
          onPaginationModelChange={(newModel) =>
            setPaginationModelStatic(newModel)
          }
          checkboxSelection
          processRowUpdate={processRowUpdate}
        />

        {openConfirmDialog && (
          <RejectConfirmDialog
            clickOnReject={() => {
              handleRejectConfirmation(openConfirmDialog);
            }}
            loading={loadingConfirm}
            onClose={() => {
              setOpenConfirmDialog(false);
            }}
          />
        )}

        {openAssignedStaffsDialog && (
          <AssignStaffDialog
            row={openAssignedStaffsDialog}
            onClose={() => {
              setOpenAssignedStaffsDialog(false);
            }}
          />
        )}

        {openFileUploadDialog && (
          <FileUploadDialog
            row={openFileUploadDialog}
            onClose={() => {
              setOpenFileUploadDialog(false);
            }}
          />
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => {
            setAnchorEl(null);
            setSelectedFiles([]);
          }}
        >
          {selectedFiles.map((file, idx) => {
            const filename = `Fájl ${idx + 1}`;
            const fileUrl = file.link;
            const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fileUrl);
            const isPdf = /\.pdf$/i.test(fileUrl);

            const handleFileClick = () => {
              if (isImage || isPdf) {
                window.open(fileUrl, '_blank'); // új lapon nyit
              } else {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.download = filename;
                link.click();
              }
            };

            return (
              <MenuItem key={idx} onClick={handleFileClick}>
                {filename}
              </MenuItem>
            );
          })}
        </Menu>
      </Box>
    </>
  );
};

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}
